import React, { useState } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Container,
  Button,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Home as HomeIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useCurrentUser } from '../../hooks/useApi';
import { DeliverableTemplate, TemplateShare } from '@horwath-htl/shared-types';
import { TemplateEditDialog } from '../../components/deliverables/TemplateEditDialog';
import { TemplateCloneDialog } from '../../components/deliverables/TemplateCloneDialog';
import { TemplateShareDialog } from '../../components/deliverables/TemplateShareDialog';
import { TemplateDeleteConfirmDialog } from '../../components/deliverables/TemplateDeleteConfirmDialog';

export const DeliverablesTemplateDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { data: user } = useCurrentUser();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Fetch template details
  const {
    data: templateData,
    isLoading: templateLoading,
    refetch: refetchTemplate,
  } = useQuery({
    queryKey: ['template', id],
    queryFn: async () => {
      const response = await authenticatedFetch(`/api/deliverables/templates/${id}`);
      if (!response?.ok) throw new Error('Failed to fetch template');
      return await response.json();
    },
    enabled: !!id,
  });

  // Fetch template shares (if owner)
  const { data: sharesData } = useQuery({
    queryKey: ['templateShares', id],
    queryFn: async () => {
      const response = await authenticatedFetch(`/api/deliverables/templates/${id}/shares`);
      if (!response?.ok) return { shares: [] };
      return await response.json();
    },
    enabled: !!id && templateData?.template?.owner_id === user?.id,
  });

  // Fetch deliverables using this template
  const { data: deliverablesData, isLoading: deliverablesLoading } = useQuery({
    queryKey: ['templateDeliverables', id],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `/api/deliverables/templates/${id}/deliverables?pageSize=50`
      );
      if (!response?.ok) throw new Error('Failed to fetch deliverables');
      return await response.json();
    },
    enabled: !!id,
  });

  const template: DeliverableTemplate | undefined = templateData?.template;
  const shares: TemplateShare[] = sharesData?.shares || [];
  const deliverables = deliverablesData?.deliverables || [];

  // Permission calculations
  const isOwner = template?.owner_id === user?.id;
  const isAdmin = user?.permissions?.includes('deliverables.all');
  const canEdit = isOwner || isAdmin;
  const canDelete = isOwner && template?.type === 'personal';
  const canShare = isOwner && template?.type === 'personal';

  // Deliverables DataGrid columns
  const deliverablesColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      renderCell: (params) => {
        return new Date(params.value).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => {
        const statusColors: Record<string, 'info' | 'success' | 'warning' | 'error'> = {
          in_progress: 'info',
          ready_for_generation: 'success',
          generating: 'warning',
          completed: 'success',
          failed: 'error',
        };
        return (
          <Chip
            label={params.value}
            color={statusColors[params.value] || 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'documents_count',
      headerName: 'Documents',
      width: 120,
      align: 'center',
    },
  ];

  // Shares DataGrid columns
  const sharesColumns: GridColDef[] = [
    {
      field: 'user_name',
      headerName: 'User Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'user_email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'can_edit',
      headerName: 'Can Edit',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
  ];

  const handleDownload = async () => {
    if (!template) return;
    try {
      const response = await authenticatedFetch(
        `/api/deliverables/templates/${template.id}/download`
      );
      if (response?.ok) {
        const data = await response.json();
        if (data.success && data.downloadUrl) {
          // Open SAS URL in new tab to trigger download
          window.open(data.downloadUrl, '_blank');
        }
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleCloneSuccess = (newTemplateId: string) => {
    navigate(`/admin/deliverables/templates/${newTemplateId}`);
  };

  if (templateLoading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!template) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">Template not found or access denied</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Link
            color="inherit"
            href="/admin/deliverables/templates"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/deliverables/templates');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Templates
          </Link>
          <Typography color="text.primary">{template.name}</Typography>
        </Breadcrumbs>

        {/* Header with Badges */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {template.name}
          </Typography>
          <Box display="flex" gap={1}>
            <Chip
              label={template.type}
              color={
                template.type === 'global'
                  ? 'primary'
                  : template.type === 'personal'
                  ? 'secondary'
                  : 'info'
              }
              size="small"
            />
            <Chip
              label={template.is_active ? 'Active' : 'Inactive'}
              color={template.is_active ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setEditDialogOpen(true)}
            >
              Edit
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => setCloneDialogOpen(true)}
          >
            Clone
          </Button>
          {canShare && (
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={() => setShareDialogOpen(true)}
            >
              Share
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download
          </Button>
        </Box>

        {/* Template Details */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Template Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1">{template.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Type
              </Typography>
              <Typography variant="body1">
                <Chip label={template.type} size="small" />
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Language
              </Typography>
              <Typography variant="body1">{template.language_code}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Version
              </Typography>
              <Typography variant="body1">{template.version}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body1">
                <Chip
                  label={template.is_active ? 'Active' : 'Inactive'}
                  color={template.is_active ? 'success' : 'default'}
                  size="small"
                />
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Created By
              </Typography>
              <Typography variant="body1">{template.created_by}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Created Date
              </Typography>
              <Typography variant="body1">
                {new Date(template.created_at).toLocaleString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Updated Date
              </Typography>
              <Typography variant="body1">
                {new Date(template.updated_at).toLocaleString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </Typography>
            </Grid>
            {template.description && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1">{template.description}</Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Blob Path
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {template.blob_path}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Shared With (if owner) */}
        {isOwner && template.type === 'personal' && (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Shared With</Typography>
              <Button size="small" onClick={() => setShareDialogOpen(true)}>
                Manage Sharing
              </Button>
            </Box>
            {shares.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                This template is not currently shared with anyone
              </Typography>
            ) : (
              <Box sx={{ height: 300 }}>
                <DataGrid
                  rows={shares}
                  columns={sharesColumns}
                  getRowId={(row) => row.user_id}
                  disableRowSelectionOnClick
                  hideFooter
                />
              </Box>
            )}
          </Paper>
        )}

        {/* Deliverables Using This Template */}
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Deliverables Using This Template
          </Typography>
          {deliverablesLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : deliverables.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No deliverables are currently using this template
            </Typography>
          ) : (
            <Box sx={{ height: 400 }}>
              <DataGrid
                rows={deliverables}
                columns={deliverablesColumns}
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                disableRowSelectionOnClick
              />
            </Box>
          )}
        </Paper>
      </Box>

      {/* Dialogs */}
      {template && (
        <>
          <TemplateEditDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            template={template}
            onSuccess={refetchTemplate}
          />
          <TemplateCloneDialog
            open={cloneDialogOpen}
            onClose={() => setCloneDialogOpen(false)}
            sourceTemplate={template}
            onSuccess={handleCloneSuccess}
          />
          <TemplateShareDialog
            open={shareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
            templateId={template.id}
          />
          <TemplateDeleteConfirmDialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            templateId={template.id}
            templateName={template.name}
          />
        </>
      )}
    </Container>
  );
};
